// German copy for the pricing pages ("Sie", like the site's de locale). Same shape as copy.en.ts (the source of
// truth); keep the two in step. Plan names (Lite, Pro, Max, Pay as you go, Enterprise) are product names and stay
// in English, as they do in the app. "Vorschau zuerst", "FTP-Server auf Abruf" and "Web-Uploader" as on
// /preview-first-uploads and /on-demand-ftp.

import type { PricingCopy } from "./copy.en";

const de: PricingCopy = {
  common: {
    perMonth: "/Monat",
    onDemandFtpLink: "So funktioniert FTP auf Abruf",
    aMonth: "im Monat",
    today: "Heute",
    fromChangeDate: "Ab 1. November",
    changeDate: "1. November 2026",
    gb: "{n} GB",
    tb: "{n} TB",
    mostPopular: "Am beliebtesten",
    renamedPlan: "{from} (jetzt {to})",
    planFeatures: {
      free: [
        {
          text: "Ihr eigenes Branding, “Powered by Photohawk”",
          note: "Ihr Logo und Ihre Farben in jeder Galerie. Ein kleines “Powered by Photohawk”-Abzeichen steht am Fuß Ihrer Galerien und der Anmeldeseite Ihres Arbeitsbereichs. Pro und Max haben kein Abzeichen.",
        },
        "Team mit bis zu 3 Personen",
        "Support über das Hilfecenter",
        {
          text: "FTP-Server auf Abruf",
          note: "Wenn FTP unverzichtbar ist, starten Sie Ihren eigenen Server im Web-Uploader, unter Ihrer eigenen Photohawk-Adresse, die sich nie ändert. Er schaltet sich nach 1 Stunde ohne Aktivität automatisch ab.",
        },
        {
          text: "Uploads mit Vorschau zuerst",
          note: "Die Galerie geht in Minuten mit leichten Vorschaubildern online, während die Originale nachkommen, für Verkäufe ebenso wie für kostenlose Downloads.",
        },
      ],
      pro: [
        {
          text: "Ihr eigenes Branding",
          note: "Ihr Logo und Ihre Farben, ohne Photohawk-Abzeichen.",
        },
        "Eigene Domain",
        "Team mit bis zu 10 Personen",
        "Besucheranalysen",
        {
          text: "FTP-Server auf Abruf",
          note: "Wenn FTP unverzichtbar ist, starten Sie Ihren eigenen Server im Web-Uploader, unter Ihrer eigenen Photohawk-Adresse, die sich nie ändert. Er schaltet sich nach 1 Stunde ohne Aktivität automatisch ab.",
        },
        {
          text: "Uploads mit Vorschau zuerst",
          note: "Die Galerie geht in Minuten mit leichten Vorschaubildern online, während die Originale nachkommen, für Verkäufe ebenso wie für kostenlose Downloads.",
        },
        "Support per E-Mail",
      ],
      max: [
        "Alles aus Pro",
        "Video-Upload",
        "Umsatzaufteilung",
        "Mehrere Marken",
        "Unbegrenzt viele Teammitglieder und Rollen",
        "Besucheranalysen",
        {
          text: "FTP-Server auf Abruf",
          note: "Wenn FTP unverzichtbar ist, starten Sie Ihren eigenen Server im Web-Uploader, unter Ihrer eigenen Photohawk-Adresse, die sich nie ändert. Er schaltet sich nach 1 Stunde ohne Aktivität automatisch ab.",
        },
        {
          text: "Uploads mit Vorschau zuerst",
          note: "Die Galerie geht in Minuten mit leichten Vorschaubildern online, während die Originale nachkommen, für Verkäufe ebenso wie für kostenlose Downloads.",
        },
        "Bevorzugter Support",
      ],
    },
    planTaglines: {
      free: "Für den Einstieg und gelegentliche Events.",
      pro: "Für Arbeitsbereiche, die regelmäßig unter eigener Marke verkaufen.",
      max: "Für Studios, Teams und hohe Volumen: die niedrigste Provision, der meiste Speicherplatz, Video und bevorzugter Support.",
    },
    countries: {
      uk: "Vereinigtes Königreich",
      my: "Malaysia",
      ph: "Philippinen",
      mx: "Mexiko",
      co: "Kolumbien",
      eur: "Portugal und Eurozone",
      us: "Vereinigte Staaten",
      ca: "Kanada",
    },
    inCountry: {
      uk: "im Vereinigten Königreich",
      my: "in Malaysia",
      ph: "auf den Philippinen",
      mx: "in Mexiko",
      co: "in Kolumbien",
      eur: "in Portugal und der Eurozone",
      us: "in den Vereinigten Staaten",
      ca: "in Kanada",
    },
  },

  pricing: {
    seo: {
      title: "Preise | Photohawk",
      description:
        "Niedrigere Provisionen in jedem Plan für Eventfotografie: 16% bei Lite, 14% bei Pro und 12% bei Max. Speicherplatz und kostenlose Downloads inklusive.",
    },
    notice: "Neue Preise ab 1. November 2026.",
    noticeLink: "Was sich ändert",
    hero: {
      badge: "Preise",
      title: "Niedrigere Provisionen in jedem Plan.",
      titleHighlight: "Ab nur {rate}.",
      subtitle:
        "Verkaufen Sie mit 16% bei Lite, 14% bei Pro oder 12% bei Max. Transaktionsgebühren funktionieren genau wie heute.",
      events: "Dieselben Pläne für jede Art von Event: Läufe und Radrennen, Spiele, Shows, Festivals, Abschlussfeiern und Konferenzen.",
    },
    rate: {
      heading: "Ihre Provision in jedem Plan",
      planLabel: "bei {plan}",
      points: [
        "Kartengebühren kommen separat hinzu und funktionieren genau wie heute.",
        "Unsere Provision ist die einzige Photohawk-Gebühr bei einem Verkauf.",
        "Bestehende individuelle Vereinbarungen gelten weiter, bis sie enden.",
      ],
    },
    plans: {
      heading: "Wählen Sie Ihren Plan",
      subtitle:
        "Monatliche Pläne in US-Dollar, ohne Vertragsbindung. Jede Stufe senkt Ihre Provision und bringt mehr Speicherplatz und kostenlose Downloads.",
      sellingLabel: "Verkaufen",
      sellingLine: "{rate} Provision auf Verkäufe",
      sellingNote: "Zuzüglich Transaktionsgebühren, wie heute",
      freeDownloadsLabel: "Kostenlose Downloads",
      freeDownloadsLine: "{count} Fotos im Monat inklusive",
      overageLine: "danach $8 pro 1.000 Fotos",
      storageLabel: "Speicherplatz",
      storageLine: "{storage} inklusive",
      storageNote: "danach Pakete ab $5 im Monat",
      includedLabel: "Inklusive",
      cta: "Loslegen",
      ctaFor: "mit {plan}",
      planChange:
        "Planwechsel: Die niedrigere Provision eines Plans gilt ab Ihrem ersten vollen Monat darin. Ein Wechsel in einen kleineren Plan wird zum Monatsende wirksam.",
    },
    standard: {
      heading: "In jedem Plan enthalten",
      subtitle: "Das bekommt jeder Plan, auch Lite für $0 im Monat.",
      items: [
        { id: "selfie", title: "Selfie-Suche", body: "Käufer machen ein Selfie und sehen jedes Foto, auf dem sie sind." },
        { id: "bib", title: "Suche nach Startnummer und Uhrzeit", body: "Fotos nach Startnummer oder Aufnahmezeit finden." },
        { id: "preview", title: "Vorschauschutz", body: "Screenshot-Schutz mit Antippen zum Aufdecken, damit Vorschauen verkaufen, statt kopiert zu werden." },
        {
          id: "uploader",
          title: "Web-Uploader und Photohawk Studio",
          body: "Der Standardweg zum Hochladen: Er sendet viele Fotos gleichzeitig, passt sich Ihrer Verbindung an und macht nach Abbrüchen von selbst weiter. In jedem Plan können leichte Vorschaubilder zuerst hochgehen, sodass Ihre Galerie in Minuten online ist. Studio läuft heute in Ihrem Webbrowser; die Photohawk Studio-App für Mac, iPhone und iPad erscheint im November 2026.",
        },
        { id: "whatsapp", title: "Zustellung per WhatsApp, ein Photohawk-Service für Käufer", body: "Käufer können an der Kasse gegen einen kleinen Aufpreis wählen, ihre Fotos per WhatsApp zu erhalten, fertig zum Teilen. Photohawk bietet das in jeder Galerie an: Sie müssen nichts einrichten, bepreisen oder abschalten." },
      ],
    },
    storage: {
      heading: "Speicherplatz",
      subtitle:
        "Jeder Plan enthält Speicherplatz. Speichern Sie mehr und ist eine Karte hinterlegt, werden Pakete automatisch hinzugefügt: Abgerechnet wird die günstigste Kombination, die Ihren Bedarf abdeckt. Keine Karte hinterlegt? Neue Uploads pausieren, bis Sie eine hinzufügen; nichts wird gelöscht und Ihre Galerien verkaufen weiter.",
      includedHeading: "Inklusive",
      packsHeading: "Zusätzliche Speicherpakete",
      packPrice: "${price} im Monat",
      examplesHeading: "So addieren sich Pakete",
      example: "{plan} mit {stored} gespeichert: {over} darüber, abgerechnet als {mix} = ${price} im Monat",
      note: "Wir schicken Ihnen eine E-Mail, wenn Sie 80% Ihres Speicherplatzes erreichen. Pakete entfallen, wenn Sie Platz freigeben.",
    },
    freeDownloads: {
      heading: "Galerien mit kostenlosen Downloads",
      subtitle:
        "Teilen Sie Galerien, aus denen Ihre Käufer kostenlos herunterladen. Jeder Plan enthält Fotos pro Monat; danach kosten sie in jedem Plan $8 pro 1.000 Fotos ($0,008 pro Foto).",
      line: "{count} Fotos im Monat",
      note: "Das Kontingent beginnt jeden Monat neu, und Galerien funktionieren auch darüber hinaus weiter.",
    },
    example: {
      heading: "Was Ihnen bleibt",
      subtitle: "Eine britische Bestellung, per Karte bezahlt, über Ihr eigenes Stripe-Konto, in jedem Plan. Probieren Sie einen anderen Betrag aus.",
      orderLabel: "Bestellwert",
      order: "Bestellung",
      cardFee: "Stripe-Kartengebühr",
      cardFeeNote: "Von Stripe Ihrem Konto berechnet, z. B. britische Karten 1,5% + 20p",
      colPlan: "Plan",
      colCommission: "Photohawk-Provision",
      colKeep: "Ihnen bleiben etwa",
      planCell: "{plan} ({rate})",
      note: "Die Stripe-Gebühr hängt von der Karte ab: Internationale Karten und Währungsumrechnung kosten meist mehr. Unsere Provision ist die einzige Photohawk-Gebühr.",
    },
    cardFees: {
      heading: "So funktionieren Kartengebühren",
      intro: "Karten- und Zahlungsabwicklungsgebühren funktionieren genau wie heute, getrennt von unserer Provision.",
      items: [
        {
          id: "stripe",
          title: "Über Ihr eigenes Stripe-Konto",
          body: "Im Vereinigten Königreich, in der EU, in den USA und anderen Stripe-Ländern berechnet Stripe seine Standardgebühr für die Zahlungsabwicklung direkt Ihrem Stripe-Konto.",
        },
        {
          id: "managed",
          title: "Wo Photohawk die Zahlung für Sie abwickelt",
          body: "In Malaysia, auf den Philippinen, in Mexiko, Kolumbien, Ecuador und anderen Ländern, in denen Käufer mit lokalen Wallets bezahlen, wird wie heute eine Gebühr für die Zahlungsabwicklung zu kostenbasierten lokalen Sätzen abgezogen.",
        },
      ],
      linkLabel: "Kartengebühren nach Land ansehen",
    },
    compare: {
      heading: "Alle Funktionen im Vergleich",
      subtitle: "Alles in Photohawk Studio, Plan für Plan. Studio funktioniert heute in jedem Plan in Ihrem Webbrowser.",
      featureColumn: "Funktion",
      included: "Inklusive",
      notIncluded: "Nicht inklusive",
      linkLabel: "Alle Funktionen im Vergleich",
      sections: {
        selling: "Verkaufen",
        finding: "Fotos finden",
        galleries: "Galerien",
        brand: "Ihre Marke",
        insights: "Auswertungen",
        team: "Team",
        uploading: "Hochladen",
        storage: "Speicherplatz",
        support: "Support",
      },
      rows: {
        commission: { label: "Provision auf Verkäufe", note: "Unsere einzige Gebühr bei einem Verkauf. Kartengebühren funktionieren genau wie heute." },
        products: { label: "Digitale Downloads, Abzüge und Produkte", note: "Ihre eigenen Produkte und Preise." },
        packages: { label: "Pakete und Bundles", note: "" },
        coupons: { label: "Gutscheine", note: "" },
        preorder: { label: "Vorbestellpreise", note: "" },
        whatsapp: { label: "Zustellung per WhatsApp (ein Photohawk-Service für Käufer)", note: "Ein optionales Extra, das Käufer an der Kasse bezahlen, damit ihre Fotos fertig zum Teilen per WhatsApp ankommen. Von Photohawk in jeder Galerie angeboten; keine Einstellung, die Sie steuern." },
        payouts: { label: "Auszahlungen", note: "Auf Ihr eigenes Stripe-Konto oder von Photohawk ausgezahlt, wo wir die Zahlung für Sie abwickeln." },
        revenueSplit: { label: "Umsatzaufteilung", note: "Teilen Sie die Verkäufe einer Galerie mit Fotografen, Teams, Vereinen oder Sponsoren. Photohawk berechnet jeden Anteil und zahlt ihn für Sie aus." },
        selfie: { label: "Selfie-Suche", note: "" },
        bib: { label: "Suche nach Startnummer und Text", note: "Fotos nach Startnummer oder nach Text im Foto finden." },
        time: { label: "Suche nach Uhrzeit", note: "" },
        find: { label: "Gelistet in „Fotos finden“", note: "Käufer finden Ihre öffentlichen Galerien auf photohawk.com." },
        profiles: { label: "Gespeicherte Suchen", note: "Ein Selfie, eine Nummer oder einen Text speichern und neue Treffer sehen." },
        faceData: { label: "Gesichtsdaten für die Selfie-Suche", note: "Ab 1. November 2026 standardmäßig unbegrenzt oder für einen Zeitraum Ihrer Wahl." },
        galleries: { label: "Galerien und Sammlungen", note: "So viele, wie Sie brauchen." },
        private: { label: "Private Galerien und Passwörter", note: "" },
        protection: { label: "Vorschauschutz", note: "Screenshot-Schutz mit Antippen zum Aufdecken." },
        watermarks: { label: "Wasserzeichen", note: "" },
        freeDownloads: { label: "Inklusive Fotos zum kostenlosen Download", note: "Das Kontingent beginnt jeden Monat neu." },
        freeOverage: { label: "Fotos zum kostenlosen Download über dem Kontingent", note: "Galerien funktionieren weiter." },
        video: { label: "Video-Upload", note: "" },
        address: { label: "Ihre Photohawk-Adresse", note: "ihrname.photohawk.com" },
        customDomain: { label: "Eigene Domain", note: "Ihre Galerien unter Ihrer eigenen Webadresse." },
        pages: { label: "Seiten", note: "Ihre eigenen Seiten neben Ihren Galerien." },
        emails: { label: "Eigene E-Mail-Vorlagen", note: "Die E-Mails bearbeiten, die Käufer aus jeder Galerie erhalten." },
        logo: { label: "Ihr Logo und Ihre Farben", note: "In Ihren Galerien, auf Ihren Seiten und in Ihren E-Mails." },
        noBadge: { label: "Kein “Powered by Photohawk”-Abzeichen", note: "Bei Lite steht ein kleines Abzeichen am Fuß Ihrer Galerien und Ihrer Anmeldeseite." },
        live: { label: "Live-Besucher", note: "Wie viele Personen eine Galerie gerade ansehen." },
        analytics: { label: "Besucheranalysen", note: "Nutzer und Besuche im Zeitverlauf, Galerie für Galerie." },
        sales: { label: "Bestellungen und Verkäufe im Zeitverlauf", note: "" },
        collectionDownloads: { label: "Downloads nach Sammlung", note: "" },
        people: { label: "Personen in Ihrem Arbeitsbereich", note: "Galerie-Mitwirkende zählen mit. Personen, die bereits in Ihrem Arbeitsbereich sind, behalten ihren Zugang." },
        roles: { label: "Rollen", note: "Zugriff als Inhaber, Administrator, nur Upload oder nur Verkauf." },
        collaborators: { label: "Galerie-Mitwirkende", note: "" },
        webUploader: { label: "Web-Uploader", note: "Sendet viele Fotos gleichzeitig und macht von selbst weiter." },
        previewsFirst: { label: "Vorschau zuerst", note: "Galerien gehen in Minuten mit leichten Vorschaubildern online, während die Originale nachkommen." },
        soldFirst: { label: "Verkaufte Fotos zuerst", note: "Ein Foto, das gekauft oder heruntergeladen wird, bevor sein Original da ist, kommt als Nächstes dran." },
        originalsBy: { label: "„Originale bis“-Datum für Käufer", note: "Sagen Sie Käufern, wann die Originale bereitstehen." },
        ftp: { label: "FTP-Server auf Abruf", note: "Wenn FTP unverzichtbar ist: Ihr eigener Server, gestartet im Web-Uploader. Er schaltet sich nach 1 Stunde ohne Aktivität automatisch ab." },
        cloudImport: { label: "Import aus Dropbox und Google Drive", note: "" },
        studioApp: { label: "Photohawk Studio-App für Mac, iPhone und iPad", note: "Studio funktioniert heute in Ihrem Webbrowser." },
        included: { label: "Inklusive Speicherplatz", note: "" },
        packs: { label: "Zusätzlicher Speicherplatz", note: "Mit hinterlegter Karte automatisch hinzugefügt, abgerechnet als günstigste Kombination." },
        you: { label: "Support für Ihren Arbeitsbereich", note: "" },
        buyers: { label: "Photohawk antwortet Ihren Käufern", note: "Fragen von Käufern gehen an den Photohawk-Support, außer Sie hinterlegen Ihre eigene Adresse." },
      },
      values: {
        rate: "{rate}",
        storage: "{storage}",
        perMonth: "{n} im Monat",
        upToPeople: "Bis zu {n}",
        unlimited: "Unbegrenzt",
        upToDomains: "Bis zu {n}",
        months: "{n} Monate",
        fromNovember: "Ab November 2026",
        packs: "Ab ${usd} pro {gb} GB im Monat",
        overage: "${usd} pro 1.000",
        supportHelp: "Hilfecenter",
        supportEmail: "E-Mail",
        supportPriority: "Bevorzugt",
      },
    },
    faq: {
      heading: "Fragen",
      items: [
        {
          q: "Sind Kartengebühren enthalten?",
          a: "Nein. Kartengebühren berechnet der Zahlungsanbieter genau wie heute: Stripe Ihrem eigenen Konto, oder sie werden zu kostenbasierten lokalen Sätzen abgezogen, wo Photohawk die Zahlung für Sie abwickelt. Unsere Provision (16% bei Lite, 14% bei Pro, 12% bei Max) ist die einzige Photohawk-Gebühr.",
        },
        {
          q: "Ist die Selfie-Suche enthalten?",
          a: "Ja, in jedem Plan, auch bei Lite. Ebenso die Suche nach Startnummer und Uhrzeit, der Vorschauschutz und der Web-Uploader. Die Photohawk Studio-App für Mac, iPhone und iPad kommt in jeden Plan, sobald sie im November 2026 erscheint.",
        },
        {
          q: "Kann ich den Plan jederzeit wechseln?",
          a: "Ja. Die Pläne sind monatlich, ohne Jahresvertrag. Wechseln Sie in einen Plan mit niedrigerer Provision, gilt diese ab Ihrem ersten vollen Monat darin. Ein Wechsel in einen kleineren Plan wird zum Monatsende wirksam.",
        },
        {
          q: "Ich habe eine individuelle Vereinbarung. Ändert sie sich?",
          a: "Nein. Bestehende individuelle Vereinbarungen gelten weiter, bis sie enden.",
        },
        {
          q: "Was passiert mit kostenlosen Downloads über dem Kontingent?",
          a: "Ihre Galerien mit kostenlosen Downloads funktionieren weiter. Jeden Monat enthält Ihr Plan 1.000 Fotos bei Lite, 5.000 bei Pro und 10.000 bei Max. Danach kosten sie in jedem Plan $8 pro 1.000 Fotos ($0,008 pro Foto).",
        },
        {
          q: "Was, wenn ich mehr Speicherplatz brauche?",
          a: "Lite enthält 150 GB, Pro 1 TB und Max 3 TB. Darüber werden mit hinterlegter Karte automatisch Pakete hinzugefügt: +100 GB für $5, +1 TB für $25 oder +5 TB für $100 im Monat, abgerechnet als günstigste Kombination, die Ihren Bedarf abdeckt. Ohne hinterlegte Karte pausieren neue Uploads, bis Sie eine hinzufügen: Nichts wird gelöscht und Ihre Galerien verkaufen weiter.",
        },
        {
          q: "Was bekomme ich mit Max?",
          a: "Die niedrigste Provision, 12%, dazu 3 TB Speicherplatz, 10.000 Fotos zum kostenlosen Download im Monat, Video-Upload, unbegrenzt viele Teammitglieder, Besucheranalysen und bevorzugten Support.",
        },
        {
          q: "Wie viele Personen können in meinem Arbeitsbereich sein?",
          a: "Bis zu 3 Personen bei Lite, bis zu 10 bei Pro und so viele, wie Sie möchten, bei Max. Sie haben bereits mehr Personen in Ihrem Arbeitsbereich? Sie behalten ihren Zugang: Das Limit gilt nur, wenn Sie jemand Neues einladen.",
        },
        {
          id: "ftp",
          q: "Kann ich weiterhin per FTP hochladen?",
          a: "Ja, wenn FTP unverzichtbar ist. Der Web-Uploader ist jetzt in jedem Plan der Standardweg zum Hochladen: Er sendet viele Fotos gleichzeitig, weit mehr als ein FTP-Programm wie FileZilla (standardmäßig 2 gleichzeitig und höchstens 10), und macht von selbst weiter, wenn die Verbindung abbricht. Wenn Sie FTP weiterhin brauchen, kann jede Person in Ihrem Arbeitsbereich in jedem Plan im Web-Uploader ihren eigenen FTP-Server auf Abruf starten, unter einer persönlichen Photohawk-Adresse, die sich nie ändert; er schaltet sich nach 1 Stunde ohne Aktivität automatisch ab. Die gemeinsamen FTP-Server werden am 1. November 2026 abgeschaltet.",
        },
        {
          q: "Gehört Photohawk Friends & Family zu einem Plan?",
          a: "Nein. Friends & Family, unsere App für die Fotos Ihrer Familie, ist eine eigene App, die bald erscheint, und gehört zu keinem Studio-Plan. Sie betreiben Galerien und Uploads? Das ist Photohawk Studio: Es funktioniert heute in jedem Plan in Ihrem Webbrowser, und die Studio-App für Mac, iPhone und iPad erscheint im November 2026.",
        },
      ],
    },
  },

  changes: {
    seo: {
      title: "Die Preise ändern sich am 1. November 2026 | Photohawk",
      description:
        "Ab 1. November 2026 sinkt die Provision in jedem Photohawk-Plan, Pro und Max bekommen mehr Speicherplatz, und Fotos zum kostenlosen Download sind jeden Monat inklusive.",
    },
    hero: {
      badge: "Preisänderung",
      title: "Die Preise ändern sich am 1. November 2026.",
      titleHighlight: "Niedrigere Provision in jedem Plan.",
      subtitle:
        "Die Provision sinkt in jedem Plan, Pro und Max bekommen mehr Speicherplatz, und Fotos zum kostenlosen Download sind jetzt jeden Monat inklusive. Sie müssen nichts tun.",
      ctaPrimary: "Die neuen Preise ansehen",
      ctaSecondary: "Ihren Plan vergleichen",
    },
    why: {
      heading: "Was besser wird",
      items: [
        {
          id: "rates",
          title: "Niedrigere Provision in jedem Plan",
          body: "Die Provision sinkt in jedem Plan, um 1,5 bis 9 Prozentpunkte. Pay as you go und Lite werden zum neuen Lite, ohne Monatsgebühr und mit 16%, Pro geht auf 14%, und Enterprise, jetzt Max, auf 12%.",
        },
        {
          id: "storage",
          title: "Mehr Speicherplatz in jedem Plan",
          body: "Lite steigt von 100 GB auf 150 GB, Pro von 500 GB auf 1 TB und Max von 1 TB auf 3 TB. Die 25 GB von Pay as you go werden zu 150 GB bei Lite.",
        },
        {
          id: "downloads",
          title: "Fotos zum kostenlosen Download inklusive",
          body: "Heute wird jedes Foto in einer Galerie mit kostenlosen Downloads berechnet. Ab 1. November sind 1.000 bis 10.000 im Monat inklusive, danach kosten sie $8 pro 1.000: gleich viel oder weniger als heute, in jedem Plan.",
        },
        {
          id: "simple",
          title: "Einfachere Preise",
          body: "Drei Pläne statt vier, eine Provision pro Plan und zusätzlicher Speicherplatz in einfachen Paketen.",
        },
        {
          id: "order",
          title: "Transaktionsgebühren unverändert",
          body: "Transaktionsgebühren funktionieren genau wie heute, getrennt von unserer Provision.",
        },
        {
          id: "deals",
          title: "Ihre Vereinbarung gilt weiter",
          body: "Wenn Sie eine individuelle Vereinbarung haben, gilt sie weiter, bis sie endet.",
        },
      ],
      honestHeading: "Der eine Preis, der steigt",
      honestBody:
        "Enterprise, jetzt Max, kostet ab Ihrer ersten Verlängerung am oder nach dem 1. November $49 im Monat (bisher $44,99). Dafür enthält es 3 TB statt 1 TB, 10.000 Fotos zum kostenlosen Download im Monat und die niedrigste Provision, 12% statt 13,5%: Sobald Sie etwa $270 im Monat verkaufen, deckt die niedrigere Provision den Unterschied mehr als ab. Pro sinkt von $29,99 auf $29.",
    },
    when: {
      heading: "Was am 1. November passiert",
      subtitle: "Ein Datum für alle. Ihr Arbeitsbereich wechselt automatisch in seinen neuen Plan.",
      moves: {
        payg: "Weiterhin $0 im Monat. 16% statt 25% und 150 GB statt 25 GB.",
        lite: "Lite bleibt und ist jetzt kostenlos: keine $16,99 im Monat mehr, 16% statt 18,5% und mehr Speicherplatz: 150 GB statt 100 GB. Brauchen Sie mehr, gibt es zusätzlichen Speicherplatz ab $5 pro 100 GB im Monat. Ihre Galerien behalten Ihr Logo und Ihre Farben, mit einem kleinen “Powered by Photohawk”-Abzeichen.",
        pro: "$29 statt $29,99 im Monat, 14% statt 16% und 1 TB statt 500 GB.",
        enterprise: "Enterprise heißt jetzt Max — derselbe Plan, neuer Name, jetzt mit 3 TB und 12% Provision. Ab Ihrer ersten Verlängerung am oder nach dem 1. November kostet er $49 statt $44,99 im Monat.",
      },
      points: [
        {
          id: "action",
          title: "Sie müssen nichts tun",
          body: "Ihr Arbeitsbereich wechselt am 1. November 2026. Galerien, Preise und Auszahlungen laufen wie gewohnt weiter.",
        },
        {
          id: "month",
          title: "Die Regel vom vollen Monat",
          body: "Wechseln Sie in einen Plan mit niedrigerer Provision, gilt diese ab Ihrem ersten vollen Monat darin. Ein Wechsel in einen kleineren Plan wird zum Monatsende wirksam.",
        },
        {
          id: "change",
          title: "Plan wechseln",
          body: "Sie können Ihren Plan jederzeit unter „Abrechnung“ in Ihrem Arbeitsbereich wechseln.",
        },
        {
          id: "deals",
          title: "Individuelle Vereinbarungen",
          body: "Bestehende individuelle Vereinbarungen gelten weiter, bis sie enden.",
        },
        {
          id: "privacy",
          title: "Selfie-Suche und Datenschutz",
          body: "Ab 1. November entscheiden Sie, wie lange die Gesichtsdaten für die Selfie-Suche aufbewahrt werden: standardmäßig unbegrenzt oder für einen Zeitraum nach dem Upload jedes Fotos. Ihre Fotos, Verkäufe und die Startnummernsuche sind nicht betroffen.",
        },
        {
          id: "team",
          title: "Ihr Team",
          body: "Lite enthält bis zu 3 Personen, Pro bis zu 10 und Max unbegrenzt viele. Sie haben bereits mehr Personen in Ihrem Arbeitsbereich? Sie behalten ihren Zugang — das Limit gilt nur, wenn jemand Neues hinzukommt.",
        },
      ],
    },
    ftp: {
      date: "1. November 2026",
      heading: "Der Web-Uploader ist jetzt der Standardweg zum Hochladen",
      body: "Die dauerhaft laufenden gemeinsamen FTP-Server werden am 1. November 2026 abgeschaltet. Der Web-Uploader ist schneller als FTP und nutzt Ihre Verbindung voll aus, in jedem Plan. Wenn FTP unverzichtbar ist, können Sie Ihren eigenen Server auf Abruf starten.",
      items: [
        {
          id: "web",
          title: "Der Web-Uploader, in jedem Plan",
          body: "Er sendet viele Fotos gleichzeitig, weit mehr als ein FTP-Programm wie FileZilla (standardmäßig 2 gleichzeitig und höchstens 10), passt sich laufend an, um Ihre Bandbreite voll zu nutzen, und macht von selbst weiter, wenn die Verbindung abbricht. In jedem Plan können leichte Vorschaubilder zuerst hochgehen, sodass Ihre Galerie in Minuten online ist.",
        },
        {
          id: "ondemand",
          title: "Wenn FTP unverzichtbar ist: in jedem Plan",
          body: "Jetzt verfügbar, in jedem Plan: Starten Sie im Web-Uploader Ihren eigenen FTP-Server auf Abruf, unter einer persönlichen Photohawk-Adresse, die sich nie ändert. Er schaltet sich nach 1 Stunde ohne Aktivität automatisch ab.",
        },
        {
          id: "studio",
          title: "Photohawk Studio, in jedem Plan",
          body: "Die Photohawk-Uploader-App für Ihren Arbeitsbereich, auf dem Desktop und unter iOS.",
        },
      ],
    },
    privacyLink: "Richtlinie zur Aufbewahrung biometrischer Daten",
    included: {
      heading: "Was in jedem Plan enthalten ist",
      everyPlan:
        "Jeder Plan: Selfie-Suche, Suche nach Startnummer und Uhrzeit, Vorschauschutz, der Web-Uploader mit Vorschau zuerst und Ihr eigener FTP-Server auf Abruf.",
      upgrades: "Pro und Max bieten zusätzlich Besucheranalysen und eine eigene Domain. Max bietet zusätzlich Video-Upload.",
    },
    compare: {
      heading: "Heute und ab 1. November",
      subtitle: "Plan für Plan, in US-Dollar.",
      rows: {
        fee: "Monatsgebühr",
        commission: "Provision auf Verkäufe",
        storage: "Inklusive Speicherplatz",
        downloads: "Fotos zum kostenlosen Download",
        extraStorage: "Zusätzlicher Speicherplatz",
      },
      downloadsToday: "{price} pro Foto",
      downloadsNext: "{count} im Monat inklusive, danach $8 pro 1.000",
      extraStorageToday: "$0,023 pro GB im Monat",
      extraStorageNext: "Pakete: +100 GB $5, +1 TB $25, +5 TB $100 im Monat",
    },
    countries: {
      heading: "Was es dort bedeutet, wo Sie verkaufen",
      subtitle: "Beispiele in Ihrer Währung. Wählen Sie ein Land.",
      selectLabel: "Land",
      examplesBadge: "Beispiele",
      orderHeading: "Eine typische Bestellung: {amount}",
      orderSubtitle: "Die durchschnittliche Bestellung {inCountry}, per Karte bezahlt. Kartengebühr {fee}, {kind}.",
      kind: {
        stripe: "von Stripe Ihrem Konto berechnet",
        managed: "abgezogen, wo Photohawk die Zahlung abwickelt",
      },
      colPlan: "Ihr Plan",
      colCommission: "Photohawk-Provision",
      colKeep: "Ihnen bleibt",
      perOrderMore: "+{amount} pro Bestellung",
      monthlyHeading: "Ein Monat Verkäufe, alles inklusive",
      monthlySubtitle:
        "Plangebühr, Provision und Kartengebühren zusammen, als Anteil Ihrer Verkäufe, im jeweils günstigsten Plan für jede Stufe.",
      level: "{amount} Umsatz im Monat",
      onPlan: "bei {plan}",
      keepMore: "Ihnen bleiben etwa {amount} mehr im Monat",
      disclaimer:
        "Nur Beispiele, auf Basis durchschnittlicher Bestellwerte und zu festen Wechselkursen umgerechnet (Oktober 2026). Ihre Zahlen hängen von Ihren Bestellungen, Karten und Ihrem Plan ab. Kosten für kostenlose Downloads und Speicherplatz sind nicht enthalten.",
    },
    faq: {
      heading: "Fragen",
      items: [
        {
          q: "Muss ich etwas tun?",
          a: "Nein. Am 1. November 2026 wechselt Ihr Arbeitsbereich automatisch in seinen neuen Plan. Ihre Galerien, Preise und Auszahlungen laufen wie gewohnt weiter.",
        },
        {
          q: "Ich nutze Lite. Was passiert?",
          a: "Lite bleibt und ist jetzt kostenlos: Ab 1. November fallen die $16,99 im Monat weg, Ihre Provision beträgt 16% statt 18,5%, und Sie bekommen mehr Speicherplatz: 150 GB statt 100 GB. Speichern Sie mehr, gibt es zusätzlichen Speicherplatz ab $5 pro 100 GB im Monat, automatisch hinzugefügt, wenn eine Karte hinterlegt ist (ohne Karte pausieren neue Uploads, bis Sie eine hinzufügen; nichts wird gelöscht). Ihre Galerien behalten Ihr Logo und Ihre Farben, mit einem kleinen “Powered by Photohawk”-Abzeichen.",
        },
        {
          q: "Was, wenn ich mehr speichere, als mein Plan enthält?",
          a: "Mit hinterlegter Karte werden Speicherpakete automatisch hinzugefügt, ab $5 pro 100 GB im Monat, abgerechnet als günstigste Kombination, die Ihren Bedarf abdeckt. Ohne hinterlegte Karte pausieren neue Uploads, bis Sie eine hinzufügen: Nichts wird gelöscht und Ihre Galerien verkaufen weiter.",
        },
        {
          q: "Ich nutze Enterprise. Was passiert?",
          a: "Enterprise heißt jetzt Max: derselbe Plan, neuer Name. Ab 1. November beträgt Ihre Provision 12% statt 13,5%, und Sie bekommen 3 TB statt 1 TB. Der neue Preis, $49 statt $44,99 im Monat, gilt ab Ihrer ersten Verlängerung am oder nach dem 1. November.",
        },
        {
          q: "Wie viele Personen können in meinem Arbeitsbereich sein?",
          a: "Bis zu 3 Personen bei Lite, bis zu 10 bei Pro und so viele, wie Sie möchten, bei Max. Sie haben bereits mehr Personen in Ihrem Arbeitsbereich? Sie behalten ihren Zugang: Das Limit gilt nur, wenn Sie jemand Neues einladen.",
        },
        {
          id: "ftp",
          q: "Was passiert mit FTP?",
          a: "Die dauerhaft laufenden gemeinsamen FTP-Server werden am 1. November 2026 abgeschaltet, und der Web-Uploader ist jetzt in jedem Plan der Standardweg zum Hochladen. Wenn FTP unverzichtbar ist, kann jede Person in Ihrem Arbeitsbereich in jedem Plan schon jetzt im Web-Uploader ihren eigenen FTP-Server auf Abruf starten, unter einer persönlichen Photohawk-Adresse, die sich nie ändert; er schaltet sich nach 1 Stunde ohne Aktivität automatisch ab.",
        },
        {
          q: "Ändert sich meine individuelle Vereinbarung?",
          a: "Nein. Bestehende individuelle Vereinbarungen gelten weiter, bis sie enden.",
        },
        {
          q: "Wenn ich den Plan wechsle, ab wann gilt die neue Provision?",
          a: "Die niedrigere Provision eines Plans gilt ab Ihrem ersten vollen Monat darin. Ein Wechsel in einen kleineren Plan wird zum Monatsende wirksam.",
        },
        {
          q: "Ändern sich die Kartengebühren?",
          a: "Nein. Karten- und Zahlungsabwicklungsgebühren funktionieren genau wie heute, getrennt von unserer Provision.",
        },
        {
          q: "Gehört Photohawk Friends & Family zu einem Plan?",
          a: "Nein. Friends & Family, unsere App für die Fotos Ihrer Familie, ist eine eigene App, die bald erscheint, und gehört zu keinem Studio-Plan. Sie betreiben Galerien und Uploads? Das ist Photohawk Studio: Es funktioniert heute in jedem Plan in Ihrem Webbrowser, und die Studio-App für Mac, iPhone und iPad erscheint im November 2026.",
        },
      ],
    },
    cta: {
      heading: "Die neuen Preise im Überblick",
      body: "Pläne, Speicherplatz, kostenlose Downloads und Kartengebühren nach Land, alles auf einer Seite.",
      button: "Die neuen Preise ansehen",
    },
  },

  app: {
    badge: "Demnächst",
    platform: "Zuerst für iPhone",
    heading: "Photohawk Friends & Family, für die Fotos Ihrer Familie",
    intro: "Friends & Family ist unsere App für alle Fotos Ihrer Familie: die, die Sie aufnehmen, und die, die Sie teilen. Sie ist eine eigene App, erscheint bald und gehört nicht zu den Studio-Plänen.",
    points: [
      { id: "private", title: "Die Fotos Ihrer Familie finden, ganz privat", body: "Fotos der Menschen, die Sie lieben, sortieren und finden, direkt auf Ihrem Telefon." },
      { id: "albums", title: "Alben, in denen alle ihre eigenen Fotos sehen", body: "Teilen Sie ein Album, und jede Person sieht nur die Fotos, auf denen sie ist, per Selfie zugeordnet: dieselbe Technik wie in Photohawk-Galerien." },
      { id: "event", title: "Die Fotos aller von einem Event sammeln", body: "Bringen Sie die Fotos von Freunden und Familie an einem Ort zusammen." },
      { id: "prints", title: "Abzüge und Fotobücher", body: "Machen Sie aus den schönsten Abzüge und Fotobücher." },
    ],
    cta: "Benachrichtigen",
    ctaNote: "Die Warteliste öffnet bald.",
    phoneAlbum: "Nur ich",
    phoneAlbumCount: "12 Fotos",
  },
};

export default de;
